import {HomeOutlined, LogoutOutlined, UserOutlined} from '@ant-design/icons';
import {Alert, Avatar, Breadcrumb, Button, Card, Form, Input, Spin} from 'antd';
import {jwtDecode} from 'jwt-decode';
import {useEffect, useRef, useState} from 'react';
import toast from 'react-hot-toast';
import {useDispatch, useSelector} from 'react-redux';
import {Navigate, useNavigate} from 'react-router-dom';

import {RQGetProfileAPI, RQUpdateAdminProfileAPI, RQUpdateUserProfileAPI} from '../../../reactQuery/apis';
import {logoutStart} from '../../../redux/authAction';
import {selectAuth} from '../../../redux/selectors';
import {LoginScreen} from '../../LoginScreen';

const ProfileScreen = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const skipRedirectRef = useRef(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [form] = Form.useForm();
  const auth = useSelector(selectAuth);
  const {loggedIn} = useSelector(state => state.auth);
  const token = auth?.tokenData;

  let decodedToken = null;
  let id = null;
  if (token) {
    try {
      decodedToken = jwtDecode(token);
      id = decodedToken?.userId;
    } catch {
      decodedToken = null;
    }
  }
  const handleLogout = () => {
    skipRedirectRef.current = true;
    dispatch(logoutStart());
    navigate('/');
  };
  useEffect(() => {
    if (!loggedIn) {setShowLoginModal(true);}
  }, [loggedIn]);

  const {data: profileDetails, isLoading, refetch, error} = RQGetProfileAPI(id, token, {
    enabled: loggedIn && !!id,
  });

  // const  = decodedToken?.role === 'ADMIN' ? RQUpdateAdminProfileAPI : RQUpdateUserProfileAPI;

  // const {mutate: updateAPI} = RQUpdateUserProfileAPI();

  const {mutate: updateProfile} = RQUpdateUserProfileAPI({
    onSuccess: response => {
      if (response) {
        refetch();
        setIsEditing(false);
        toast.success('Profile Updated Successfully!');
      } else {
        toast.error('Something went wrong!');
      }
    },
    onError: err => {
      toast.error(err?.message || 'Update failed');
    },
  });

  useEffect(() => {
    if (profileDetails) {form.setFieldsValue(profileDetails);}
  }, [profileDetails, form]);

  const onFinish = values => {
    updateProfile({token, formData: values, id});
  };

  if (!loggedIn) {
    return showLoginModal ? <LoginScreen onClose={() => setShowLoginModal(false)} /> : null;
  }

  if (isLoading) {
    return (
      <div className={'flex justify-center items-center h-screen'}>
        <Spin size={'large'} />
      </div>
    );
  }

  if (error) {return <Alert message={'Failed to load profile'} type={'error'} />;}

  return (
    <div className={'relative min-h-screen'}>
      {/* Main wrapper */}
      <div className={'relative z-10 flex justify-center md:px-4 py-8 lg:py-16'}>
        <div className={'w-full max-w-4xl gap-6 flex flex-col lg:flex-row backdrop-blur overflow-hidden rounded-md '}>

          {/* Sidebar */}
          <div className={'w-full h-96 lg:w-64 p-6 bg-primary-50 shadow-md rounded-md flex flex-col items-center gap-4'}>
            <Avatar size={80} className={'bg-blue-800 text-white text-2xl'}>
              {profileDetails?.firstName?.[0]}
              {profileDetails?.lastName?.[0]}
            </Avatar>
            <div className={'text-center'}>
              <h4 className={'text-xl font-semibold capitalize'}>
                {profileDetails?.firstName} {profileDetails?.lastName}
              </h4>
              <p className={'text-gray-500 text-sm mt-1'}>{profileDetails?.email}</p>
              <p className={'text-gray-400 text-xs mt-1'}>{decodedToken?.role}</p>
            </div>

            <div className={'flex flex-col gap-3 w-full mt-6'}>
              <Button
                onClick={() => setActiveTab('profile')}
                className={`flex items-center justify-center gap-2 ${
                  activeTab === 'profile'
                    ? 'bg-blue-800 hover:!bg-primary-900 hover:!text-primary-50 text-white'
                    : 'border border-primary-800 text-primary-950'
                }`}
                block
              >
                <UserOutlined /> My Profile
              </Button>

              <Button
                onClick={handleLogout}
                className={'mt-6 w-full bg-red-600 hover:!bg-red-700 hover:!text-red-100 text-white flex items-center justify-center gap-2'}
              >
                <LogoutOutlined /> Log Out
              </Button>
            </div>
          </div>

          {/* Content */}
          <div className={'flex-1 p-6 lg:p-8 rounded-md shadow-md bg-primary-50'}>
            {activeTab === 'profile' && (
              <div className={'bg-primary-50'}>
                <h1 className={'header'}>
        Personal Information
                </h1>

                <Form form={form} layout={'vertical'} onFinish={onFinish}>
                  <div className={'grid grid-cols-1 md:grid-cols-2 gap-2'}>
                    <Form.Item
                      name={'firstName'}
                      label={'First Name'}
                      className={'mb-2'}
                      rules={[
                        {required: true, message: 'First name is required'},
                        {whitespace: true, message: ''},
                        {pattern: /^[A-Za-z]+$/, message: 'Only letters allowed'},
                      ]}
                    >
                      <Input className={'!text-[#525252]'} disabled={!isEditing} />
                    </Form.Item>

                    <Form.Item
                      name={'lastName'}
                      className={'mb-2'}
                      label={'Last Name'}
                      rules={[
                        {required: true, message: 'Last name is required'},
                        {whitespace: true, message: ''},
                        {pattern: /^[A-Za-z]+$/, message: 'Only letters allowed'},
                      ]}
                    >
                      <Input className={'!text-[#525252]'} disabled={!isEditing} />
                    </Form.Item>

                    <Form.Item
                      name={'mobile'}
                      label={'Mobile'}
                      className={'mb-2'}
                      rules={[
                        {required: true, message: 'Enter contact number'},
                        {pattern: /^[0-9]{10}$/, message: 'Must be 10 digits'},
                      ]}
                    >
                      <Input className={'!text-[#525252]'} disabled={!isEditing} maxLength={10} />
                    </Form.Item>

                    <Form.Item
                      name={'email'}
                      label={'Email'}
                      className={'mb-2'}
                      rules={[
                        {required: true, message: 'Email is required'},
                        {pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Invalid email format'},
                      ]}
                    >
                      <Input className={'!text-[#525252]'} disabled />
                    </Form.Item>

                    <Form.Item
                      name={'street'}
                      label={'Street'}
                      className={'mb-2'}
                      rules={[
                        {required: true, message: 'Street is required'},
                        {whitespace: true, message: 'Street is required'},
                      ]}
                    >
                      <Input className={'!text-[#525252]'} disabled={!isEditing} />
                    </Form.Item>

                    <Form.Item
                      name={'apartment'}
                      label={'Apartment'}
                      className={'mb-2'}
                      rules={[
                        {required: true, message: 'Apartment is required'},
                        {whitespace: true, message: 'Apartment is required'},
                      ]}
                    >
                      <Input className={'!text-[#525252]'} disabled={!isEditing} />
                    </Form.Item>

                    <Form.Item
                      name={'pinCode'}
                      label={'Pin Code'}
                      className={'mb-2'}
                      rules={[
                        {required: true},
                        {pattern: /^[0-9]{6}$/, message: 'Must be 6 digits'},
                      ]}
                    >
                      <Input className={'!text-[#525252]'} disabled={!isEditing} />
                    </Form.Item>
                    
                    <Form.Item
                      name={'state'}
                      label={'State'}
                      className={'mb-2'}
                      rules={[
                        {required: true, message: 'State is required'},
                        {pattern: /^[A-Za-z\s]+$/, message: 'Only letters allowed'},
                      ]}
                    >
                      <Input className={'!text-[#525252]'} disabled={!isEditing} />
                    </Form.Item>

                    <Form.Item 
                      name={'city'} 
                      label={'City'}
                      className={'mb-2'}
                      rules={[{
                        required: true,
                        message: 'City is required',
                      },
                      {
                        pattern: /^[A-Za-z]+(?: [A-Za-z]+)*$/,
                        message: 'Only letters allowed, no leading/trailing spaces',
                      }]}>
                      <Input className={'!text-[#525252]'} disabled={!isEditing} />
                    </Form.Item>

                    <Form.Item 
                      name={'district'}
                      label={'District'} 
                      className={'mb-2'}
                      rules={[{required: true}, {whitespace: true, message: 'District is required'}]}>
                      <Input className={'!text-[#525252]'} disabled={!isEditing} />
                    </Form.Item>

                    <Form.Item
                      name={'address'}
                      label={'Address'}
                      className={'md:col-span-2'}
                      rules={[{required: true}, {whitespace: true, message: 'Address is required'}]}
                    >
                      <Input className={'!text-[#525252]'} TextArea rows={3} disabled={!isEditing} />
                    </Form.Item>
                  </div>

                  {isEditing && (
                    <div className={'flex justify-end gap-4 mt-4'}>
                      <Button
                        onClick={() => {
                          form.setFieldsValue(profileDetails);
                          setIsEditing(false);
                        }}
                      >
                        Cancel
                      </Button>
                      <Button htmlType={'submit'} type={'primary'}>
                        Save Changes
                      </Button>
                    </div>
                  )}
                  <div className={'w-full justify-center items-center'}>
                    {!isEditing && (
                   
                      <Button
                        className={'mt-4 px-6  bg-blue-800 hover:!text-primary-50 text-white hover:!bg-primary-900'}
                        onClick={() => setIsEditing(true)}
                      >
                      Edit Profile
                      </Button>
                    )}
                  </div>

                </Form>
              </div>
            )}
          </div>
        </div>
      </div>

      {showLoginModal && <LoginScreen onClose={() => setShowLoginModal(false)} />}
    </div>
  );
};

export default ProfileScreen;
